import { useRef, useEffect, useState } from "react";

/**
 * useUndoRedo
 * ------------
 * Tracks a history of snapshots of `state` and lets you step backwards (undo)
 * and forwards (redo) through them. Designed to sit on top of an existing
 * state value + setter (e.g. sdataGroup / setSdataGroup) without changing how
 * the rest of the app mutates that state.
 *
 * How it captures history:
 *   Every time `state` changes it is (debounced) serialized and pushed onto a
 *   stack. Bursts of rapid changes (e.g. typing) collapse into a single undo
 *   step thanks to the debounce. Changes caused by undo/redo itself are NOT
 *   re-recorded (guarded by an internal flag).
 *
 * Params:
 *   state        - the value to track (must be JSON-serializable)
 *   setState     - setter used to restore a snapshot
 *   onRestore    - optional callback(restoredState) run after a snapshot is
 *                  restored, so dependent UI state can be re-synced
 *   resetKey     - when this changes the history is cleared (e.g. projectId)
 *   debounceMs   - how long to wait before recording a snapshot (default 400)
 *   maxHistory   - cap on stored snapshots (default 100)
 *
 * Returns: { undo, redo, canUndo, canRedo, historySize, position }
 */
export function useUndoRedo({
  state,
  setState,
  onRestore,
  resetKey,
  debounceMs = 400,
  maxHistory = 100,
}) {
  // Persistent history that survives re-renders. `stack` holds serialized
  // snapshots, `index` points at the snapshot currently applied.
  const history = useRef({ stack: [], index: -1 });
  // True while we are restoring a snapshot, so the record effect skips it.
  const isTimeTravel = useRef(false);
  const debounceTimer = useRef(null);
  // Bump to force a re-render when canUndo/canRedo change.
  const [, forceRender] = useState(0);
  const bump = () => forceRender((v) => v + 1);

  const isEmpty = (val) =>
    val == null ||
    (typeof val === "object" &&
      !Array.isArray(val) &&
      Object.keys(val).length === 0);

  // Clear history when the reset key changes (e.g. switching projects).
  useEffect(() => {
    history.current = { stack: [], index: -1 };
    isTimeTravel.current = false;
    clearTimeout(debounceTimer.current);
    bump();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  // Record snapshots as `state` changes (debounced).
  useEffect(() => {
    // Ignore empty/blank baselines so undo can never wipe the survey to {}.
    if (isEmpty(state)) return;

    if (isTimeTravel.current) {
      // This change came from undo/redo — don't record it.
      isTimeTravel.current = false;
      return;
    }

    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      const snapshot = JSON.stringify(state);
      const h = history.current;

      // Skip if nothing actually changed vs the current snapshot.
      if (h.index >= 0 && h.stack[h.index] === snapshot) return;

      // Drop any "redo" future once a new edit is made.
      h.stack = h.stack.slice(0, h.index + 1);
      h.stack.push(snapshot);

      // Cap the history size.
      if (h.stack.length > maxHistory) h.stack.shift();

      h.index = h.stack.length - 1;
      bump();
    }, debounceMs);

    return () => clearTimeout(debounceTimer.current);
  }, [state, debounceMs, maxHistory]);

  const travel = (targetIndex) => {
    const h = history.current;
    if (targetIndex < 0 || targetIndex >= h.stack.length) return;

    // Cancel any pending capture so it can't clobber the stack after restore.
    clearTimeout(debounceTimer.current);

    h.index = targetIndex;
    isTimeTravel.current = true;

    const restored = JSON.parse(h.stack[targetIndex]);
    setState(restored);
    if (onRestore) onRestore(restored);
    bump();
  };

  const undo = () => travel(history.current.index - 1);
  const redo = () => travel(history.current.index + 1);

  return {
    undo,
    redo,
    canUndo: history.current.index > 0,
    canRedo: history.current.index < history.current.stack.length - 1,
    historySize: history.current.stack.length,
    position: history.current.index,
  };
}

export default useUndoRedo;
